/**
 * AuthGuard - Unified Route Protection
 * 
 * Single component that handles all authentication checks
 * - Redirects unauthenticated users to login
 * - Redirects authenticated users away from login pages
 * - Handles role-based access control
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthGuard = ({ 
  children, 
  requireAuth = false, 
  requireGuest = false,
  requiredRole = null 
}) => {
  const { isAuthenticated, isLoading, isAuthorized, getDashboardPath, isBarred, getBarredUserRedirectPath } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle barred users - redirect them to barred user message page
  if (isBarred) {
    return <Navigate to="/barred-user" replace state={{ from: location, barred: true }} />;
  }

  // Redirect authenticated users to their dashboard for specific routes
  if (isAuthenticated) {
    // Root path - redirect to dashboard
    if (location.pathname === '/') {
      return <Navigate to={getDashboardPath()} replace />;
    }
    
    // Login pages - redirect to dashboard
    if (requireGuest) {
      return <Navigate to={getDashboardPath()} replace />;
    }
    
    // Other public routes that authenticated users shouldn't access
    const publicRoutesForGuests = [
      '/vigilance-portal',
      '/vigilance-validation', 
      '/vigilance-status'
    ];
    
    if (publicRoutesForGuests.includes(location.pathname)) {
      return <Navigate to={getDashboardPath()} replace />;
    }
  }

  // For routes that require authentication (dashboards)
  if (requireAuth && !isAuthenticated) {
    // User is not logged in, redirect to home
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // For routes that require specific role
  if (requireAuth && requiredRole && !isAuthorized(requiredRole)) {
    // User doesn't have required role, redirect to their correct dashboard
    return <Navigate to={getDashboardPath()} replace />;
  }

  // All checks passed, render the component
  return children;
};

export default AuthGuard;

